import React, { useEffect, useRef, useState } from 'react';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { getViewingModelId } from '../utils/shareSession';
import { useAuth } from '../auth/AuthContext';
import { modalSecondaryBtn, modalDangerBtn, MODAL_BTN_ROW, modalCard, modalTitle, modalBody } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

const TITLE_MAX = 30;
const DESCRIPTION_MAX = 300;

const rowBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Circular arrows nested inside the same bordered box as the label text
// (not floating outside it) — width: 100% on the box itself still matches
// the "+ Add label" button's width exactly.
const carouselArrowStyle = {
  flexShrink: 0,
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.16)',
  color: '#fff',
  fontSize: 17,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

// Right-side panel listing point-annotation labels for the model currently
// showing in <Scene />. Mounted inside Scene.js (not globally in App.js like
// GlobalShareModal/QualityModal) because, unlike those, it needs a modelId —
// getViewingModelId() resolves that the same way for both the authenticated
// /scene?modelId= route and the /s/:shareId share route SharedScene.js
// renders <Scene /> into, so this one component covers both without any
// share-specific branching.
const LabelsPanel = () => {
  const { user } = useAuth();
  // A share-link visit (SharedScene.js/index.js both stamp this class on
  // <body> before Scene ever mounts — the same signal the rest of the app
  // already uses to tell a share viewer apart from a real logged-in visit)
  // is always view-only, regardless of hasRole — including for an admin who
  // happens to still be logged in on this same browser. Add/edit/delete are
  // for the real /scene?modelId= admin surface; a link handed out for
  // viewing shouldn't expose editing controls just because the person who
  // opened it also happens to have an active admin session in that tab.
  // (The backend's own canEdit/canDelete already reject a request with no
  // valid Cognito token regardless of this — this is what keeps the
  // controls from ever being offered in the first place.)
  const isSharedView = typeof document !== 'undefined' && document.body.classList.contains('shared-view');
  // Any logged-in user: they only reach splats a sysAdmin assigned them (or
  // all, as sysAdmin), and the backend checks that on every write.
  const canEdit = !!user && !isSharedView;

  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState([]);
  const [placing, setPlacing] = useState(false);
  // Set while re-picking an existing label's point ("Select new point"
  // inside the edit form) — null means the in-progress placement (if any)
  // is for a brand-new label instead. Holds the labelId plus whatever
  // title/description were in the form at the moment repositioning
  // started, so unsaved text edits survive the trip through placement
  // mode instead of being discarded. See startRepositioning and the
  // labelPointPicked handler below.
  const [placingForLabel, setPlacingForLabel] = useState(null);
  const [formState, setFormState] = useState(null); // { mode: 'create'|'edit', position?, cameraPosition?, labelId?, title, description }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [navOffsets, setNavOffsets] = useState({ top: 0, bottom: 0 });
  const [selectedLabelId, setSelectedLabelId] = useState(null);
  // Same mobile breakpoint scene.js's own toolbar layout switches at
  // (public/js/scene.js's updateTextBasedOnViewport). Below it the list
  // becomes a one-at-a-time carousel (arrows either side) instead of a
  // scrollable list — there's no room for a scrollbar-friendly list, and
  // hover-to-reveal edit/delete icons don't work on touch anyway.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 480);
  const [mobileIndex, setMobileIndex] = useState(0);

  const modelIdRef = useRef(getViewingModelId());

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep the carousel position valid as labels are added/removed/reloaded.
  useEffect(() => {
    setMobileIndex(i => (labels.length === 0 ? 0 : Math.min(i, labels.length - 1)));
  }, [labels]);

  // Keep the panel clear of the header/footer (heights vary — the bottom
  // toolbar grows when a tool's scroll-selection row is showing) by measuring
  // them directly instead of hardcoding breakpoint-specific pixel values.
  // Measures 'section.top' rather than '#top-nav' so the panel lines up
  // exactly with the profile dropdown (viewer-top.html), which anchors off
  // that same element with the same 8px gap.
  useEffect(() => {
    const resizeObserver = new ResizeObserver(measureNavOffsets);
    const observedEls = new Set();

    function measureNavOffsets() {
      const topEl = document.querySelector('section.top') || document.getElementById('top-nav');
      const bottomEl = document.getElementById('bottom-nav');
      // Viewport-relative edges (.bottom / distance from .top to the
      // viewport's bottom) rather than plain .height — robust even if the
      // nav element itself isn't flush against the very top/bottom edge.
      setNavOffsets({
        top: topEl ? topEl.getBoundingClientRect().bottom : 0,
        bottom: bottomEl ? window.innerHeight - bottomEl.getBoundingClientRect().top : 0,
      });
      // viewer-top.html/viewer-bottom.html are fetched and injected into the
      // DOM asynchronously by Scene.js, so they may not exist yet the first
      // time this runs — (re)attach the ResizeObserver as soon as they show
      // up instead of only checking once at mount.
      [topEl, bottomEl].forEach((el) => {
        if (el && !observedEls.has(el)) {
          resizeObserver.observe(el);
          observedEls.add(el);
        }
      });
    }

    measureNavOffsets();
    // Catches the async HTML injection mentioned above; cheap since it only
    // ever toggles a couple of numbers in state.
    const mutationObserver = new MutationObserver(measureNavOffsets);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measureNavOffsets);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', measureNavOffsets);
    };
  }, []);

  const loadLabels = () => {
    const modelId = modelIdRef.current;
    if (!modelId) return;
    authFetch(`${API_URL}?listLabels=true&id=${modelId}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setLabels(data.labels || []))
      .catch(() => setLabels([]));
  };

  // Tells SceneHandler.js which numbered markers to draw. While the create
  // form is open, a synthetic preview entry is appended at the end (matching
  // where it'll land once saved, since new labels sort last by CreatedAt) so
  // the just-picked point immediately shows its real numbered marker instead
  // of the plain unlabeled focal-point circle applyFocalPoint would otherwise
  // leave behind. While repositioning an EXISTING label (startRepositioning),
  // that label's own marker is patched to the newly picked point instead —
  // same "show it immediately" reasoning, but there's no separate preview
  // entry to add since the label already has a real marker; overriding its
  // position in the broadcast list moves that marker right away without
  // touching the real `labels` state (and therefore the backend) until Save.
  // Both cases clear themselves automatically the moment formState changes
  // (saved or cancelled) — cancelPlacement/submitForm's own state resets are
  // exactly what make the marker snap back to (or persist at) the right
  // place, no separate cleanup path needed here.
  useEffect(() => {
    let broadcastLabels = labels;
    if (formState?.mode === 'edit' && formState.position && formState.labelId) {
      broadcastLabels = labels.map(l => l.labelId === formState.labelId
        ? { ...l, position: formState.position, cameraPosition: formState.cameraPosition }
        : l);
    }
    const preview = formState?.mode === 'create' && formState.position
      ? [{ labelId: '__preview__', position: formState.position, cameraPosition: formState.cameraPosition, title: formState.title || 'New label' }]
      : [];
    window.dispatchEvent(new CustomEvent('labelsUpdated', { detail: [...broadcastLabels, ...preview] }));
  }, [labels, formState]);

  // scene.js dispatches this on every bottom-toolbar tool button click —
  // true only for the Labeling button, false for focal length/translate/
  // rotate. Open the panel exactly when Labeling becomes the active tool,
  // and close it (dropping any in-progress placement/form) the moment a
  // different tool is picked, rather than leaving it open across tools.
  useEffect(() => {
    const handle = (e) => {
      const active = !!e.detail;
      setOpen(active);
      if (active) {
        loadLabels();
      } else {
        setPlacing(false);
        setFormState(null);
        setConfirmDeleteId(null);
      }
    };
    window.addEventListener('labelToolSelected', handle);
    return () => window.removeEventListener('labelToolSelected', handle);
  }, []);

  // Settings should always visually win over the labels panel, but simply
  // closing the panel whenever another overlay opens (e.g. on 'dashboardNav')
  // would lose the fact the admin still wants it open — it wouldn't come back
  // once settings closes. Instead, derive visibility live from the actual DOM
  // state of the other overlays: the profile dropdown's own 'open' class
  // (viewer-top.html), and 'modal-open' on <body> (added by AccountModal/
  // GlobalShareModal/QualityModal, and — harmlessly, see selfModalOpen below —
  // by this component's own create/edit/confirm dialogs).
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bodyModalOpen, setBodyModalOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      setDropdownOpen(!!document.getElementById('profile-dropdown')?.classList.contains('open'));
      setBodyModalOpen(document.body.classList.contains('modal-open'));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
    return () => observer.disconnect();
  }, []);
  const selfModalOpen = !!(formState || confirmDeleteId);
  const suppressed = dropdownOpen || (bodyModalOpen && !selfModalOpen);

  // The title/description fields sit inside the same panel the WASD/QE/arrow
  // camera-movement shortcuts (SceneHandler.js onSmoothKeyDown/onSmoothKeyUp)
  // and the 'h'/'f' shortcuts (scene.js) are always listening on — typing a
  // shortcut letter while focused in a text field would otherwise also move
  // the camera. Every one of those listeners already bails out early on
  // window.__accountModalOpen (the same flag AccountModal.js uses for its
  // own text fields), so reuse it here rather than touching each listener.
  useEffect(() => {
    if (formState) {
      window.__accountModalOpen = true;
      return () => { window.__accountModalOpen = false; };
    }
  }, [formState]);

  // Load once on mount too, so markers render even before the panel's ever
  // been opened (viewers should see numbered hotspots immediately).
  useEffect(() => { loadLabels(); }, []);

  useEffect(() => {
    const handle = (e) => {
      const { position, cameraPosition } = e.detail || {};
      if (!position || !cameraPosition || !canEdit) return;
      setPlacing(false);
      if (placingForLabel) {
        // Repositioning an existing label ("Select new point" inside the
        // edit form) — reopen with whatever title/description were typed
        // when repositioning started (not necessarily saved yet), plus the
        // freshly picked point.
        const { labelId, title, description } = placingForLabel;
        setPlacingForLabel(null);
        setFormState({ mode: 'edit', labelId, title, description, position, cameraPosition });
      } else {
        setFormState({ mode: 'create', position, cameraPosition, title: '', description: '' });
      }
    };
    window.addEventListener('labelPointPicked', handle);
    return () => window.removeEventListener('labelPointPicked', handle);
  }, [canEdit, placingForLabel]);

  useEffect(() => {
    const handle = (e) => {
      const label = labels.find(l => l.labelId === e.detail?.labelId);
      if (!label) return;
      selectLabel(label);
    };
    window.addEventListener('labelMarkerClicked', handle);
    return () => window.removeEventListener('labelMarkerClicked', handle);
  }, [labels]);

  // scene.js dispatches this when the bottom scrollbar is used while
  // Labeling is the active tool (mirroring how it already scrolls through
  // focal length/sensor size/aspect ratio) — index is into the same
  // creation-order list broadcast via 'labelsUpdated' above (minus the
  // synthetic '__preview__' entry, which scene.js already excludes).
  useEffect(() => {
    const handle = (e) => {
      const index = e.detail?.index;
      const label = labels[index];
      if (!label) return;
      // Mirrors goPrev/goNext below (mobile carousel arrows) — mobileIndex
      // is a separate piece of state from selectedLabelId, driving what
      // the carousel actually displays (title, "N / total" counter), so
      // selectLabel() alone isn't enough to keep the shown title in sync
      // with the scroll-selected label on mobile.
      setMobileIndex(index);
      selectLabel(label);
    };
    window.addEventListener('labelScrollSelect', handle);
    return () => window.removeEventListener('labelScrollSelect', handle);
  }, [labels]);

  // The tooltip's own close button (SceneHandler.js) clears the selection
  // on its side directly — this just mirrors that back into the list's
  // highlight state so the two stay in sync.
  useEffect(() => {
    const handle = () => setSelectedLabelId(null);
    window.addEventListener('labelDeselected', handle);
    return () => window.removeEventListener('labelDeselected', handle);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('modal-open', !!formState || !!confirmDeleteId);
  }, [formState, confirmDeleteId]);

  if (!modelIdRef.current) return null;

  const startPlacement = () => {
    setPlacing(true);
    window.dispatchEvent(new CustomEvent('labelPlacementMode', { detail: true }));
  };

  // Closes the form. If a reposition was picked but not saved yet, the
  // marker already snapped back to its real saved position (see the
  // broadcast useEffect above), but the camera itself stays wherever
  // applyFocalPoint left it when the new point was picked — fly it back to
  // the label's actual saved view too, so cancelling truly discards the
  // pending move instead of leaving the camera stranded at it.
  const closeForm = () => {
    if (formState?.mode === 'edit' && formState.position) {
      const original = labels.find(l => l.labelId === formState.labelId);
      if (original) {
        window.dispatchEvent(new CustomEvent('goToLabel', { detail: { position: original.position, cameraPosition: original.cameraPosition } }));
      }
    }
    setFormState(null);
  };

  // "Select new point" button inside the edit form — same placement-mode
  // UX as Add (red circle cursor, click a point on the model). Stashes the
  // form's current title/description (possibly unsaved edits) so they
  // survive the trip through placement mode; the form itself closes while
  // picking, then reopens (via the labelPointPicked handler above, or
  // cancelPlacement below) with those preserved and the point updated.
  const startRepositioning = () => {
    if (!formState) return;
    setPlacingForLabel({ labelId: formState.labelId, title: formState.title, description: formState.description });
    setFormState(null);
    setPlacing(true);
    window.dispatchEvent(new CustomEvent('labelPlacementMode', { detail: true }));
  };

  const cancelPlacement = () => {
    setPlacing(false);
    if (placingForLabel) {
      // Reopen the edit form with whatever was there before repositioning
      // started, rather than just dropping the admin back at the list.
      const { labelId, title, description } = placingForLabel;
      setPlacingForLabel(null);
      setFormState({ mode: 'edit', labelId, title, description });
    }
    window.dispatchEvent(new CustomEvent('labelPlacementMode', { detail: false }));
  };

  // Shared by both entry points (marker click / list row click) so the
  // "which label is active" feedback — red marker border, highlighted list
  // row — always stays in sync regardless of which one was used.
  const selectLabel = (label) => {
    setSelectedLabelId(label.labelId);
    // goToLabel must fire first: it's what starts the camera tween and
    // arms the tooltip's hide-until-it-finishes deadline in SceneHandler.js.
    // labelSelected's handler there calls updateLabelMarkerPositions()
    // synchronously and immediately, which positions/shows the tooltip if
    // nothing is holding it back yet — dispatching it first left a one-frame
    // window where the tooltip could flash at the pre-tween position before
    // the tween (dispatched right after) caught up and hid it again.
    window.dispatchEvent(new CustomEvent('goToLabel', { detail: { position: label.position, cameraPosition: label.cameraPosition } }));
    window.dispatchEvent(new CustomEvent('labelSelected', { detail: { labelId: label.labelId } }));
  };

  const goToLabelRow = (label) => selectLabel(label);

  const submitForm = async () => {
    if (!formState) return;
    const title = formState.title.trim();
    const description = formState.description.trim();
    if (!title || title.length > TITLE_MAX || description.length > DESCRIPTION_MAX) return;

    setSaving(true);
    try {
      const modelId = modelIdRef.current;
      if (formState.mode === 'create') {
        const res = await authFetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            createLabel: true,
            modelId,
            position: formState.position,
            cameraPosition: formState.cameraPosition,
            title,
            description,
          }),
        });
        if (!res.ok) throw new Error();
      } else {
        // position/cameraPosition are only present when this edit went
        // through startRepositioning's "Select new point" button — a plain
        // text-only edit (no reposition) never sets them, so omit rather
        // than send stale/undefined values.
        const body = { updateLabel: true, modelId, labelId: formState.labelId, title, description };
        if (formState.position && formState.cameraPosition) {
          body.position = formState.position;
          body.cameraPosition = formState.cameraPosition;
        }
        const res = await authFetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
      }
      setFormState(null);
      loadLabels();
    } catch {
      // Leave the form open so the admin can retry.
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const labelId = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      const res = await authFetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deleteLabel: true, modelId: modelIdRef.current, labelId }),
      });
      if (!res.ok) throw new Error();
      loadLabels();
    } catch {}
  };

  // Shown at the top of the list on mobile (a fixed carousel is always
  // visible below it) and at the bottom on desktop (below the scrollable
  // list) — same block either way, just placed differently per breakpoint.
  const addLabelBlock = canEdit && (
    <div style={{ padding: '2px 12px 8px' }}>
      {!placing ? (
        <button
          type="button"
          onClick={startPlacement}
          style={{ width: '100%', padding: '9px', borderRadius: 8, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500 }}
        >
          + Add label
        </button>
      ) : (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)' }}>
          <p style={{ fontSize: 12, margin: '0 0 8px', color: 'rgba(255,255,255,0.8)' }}>Click a point on the model to place the label.</p>
          <button
            type="button"
            onClick={cancelPlacement}
            style={{ width: '100%', padding: '7px', borderRadius: 6, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 12 }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {open && !suppressed && (
        <div
          className="labels-panel"
          // Stops clicks inside the panel (e.g. "+ Add label") from ever
          // reaching SceneHandler.js's window-level placement-click listener.
          // Without this, React 18 flushes the setPlacing(true) re-render
          // synchronously mid-bubble — swapping the clicked button out of the
          // DOM before the same native click event reaches `window` — so the
          // event.target-based UI guard there sees a detached node and misses
          // it, letting the click fall through as a (wrong) placement pick.
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: navOffsets.top + 8, right: 8, width: 260, maxWidth: '85vw',
            maxHeight: `calc(100% - ${navOffsets.top + navOffsets.bottom + 16}px)`,
            background: 'rgba(10, 10, 12, 0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            color: '#fff', zIndex: 500, display: 'flex', flexDirection: 'column',
            borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontFamily: 'Inter, Arial, sans-serif',
            pointerEvents: 'auto', overflow: 'hidden',
          }}
        >
          <style>{`.labels-panel-row:hover .labels-panel-row-actions { opacity: 1 !important; }`}</style>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px 10px' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {formState ? (formState.mode === 'create' ? 'New label' : 'Edit label') : 'Labels'}
            </span>
            <button
              onClick={() => {
                if (formState) { closeForm(); return; }
                setOpen(false);
                if (placing) cancelPlacement();
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}
            >✕</button>
          </div>

          {formState ? (
            <div style={{ overflowY: 'auto', padding: '2px 18px 16px' }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Title</label>
              <input
                type="text"
                autoFocus
                value={formState.title}
                maxLength={TITLE_MAX}
                onChange={(e) => setFormState(s => ({ ...s, title: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', margin: '4px 0 2px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', color: '#fff', fontSize: 12.5, fontFamily: 'Inter, Arial, sans-serif' }}
              />
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.4)', textAlign: 'right', marginBottom: 8 }}>{formState.title.length}/{TITLE_MAX}</div>

              <label style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description</label>
              <textarea
                value={formState.description}
                maxLength={DESCRIPTION_MAX}
                rows={3}
                onChange={(e) => setFormState(s => ({ ...s, description: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', margin: '4px 0 2px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', color: '#fff', fontSize: 12.5, fontFamily: 'Inter, Arial, sans-serif', resize: 'vertical' }}
              />
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.4)', textAlign: 'right', marginBottom: 12 }}>{formState.description.length}/{DESCRIPTION_MAX}</div>

              {formState.mode === 'edit' && (
                <button
                  type="button"
                  onClick={startRepositioning}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px', marginBottom: 10, borderRadius: 8, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'Inter, Arial, sans-serif' }}
                >
                  Select new point
                </button>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={closeForm}
                  style={{ flex: 1, padding: '8px', borderRadius: 6, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'Inter, Arial, sans-serif' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving || !formState.title.trim()}
                  onClick={submitForm}
                  style={{ flex: 1, padding: '8px', borderRadius: 6, background: '#fff', color: '#1a1a1a', border: 'none', cursor: 'pointer', fontSize: 12.5, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, opacity: (saving || !formState.title.trim()) ? 0.5 : 1 }}
                >
                  {formState.mode === 'create' ? 'Add' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {isMobile && addLabelBlock}

              {labels.length === 0 ? (
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', padding: '4px 18px 14px' }}>
                  {canEdit ? 'No labels yet — click "+ Add label" to create one.' : 'No labels yet.'}
                </p>
              ) : isMobile ? (
                <div style={{ padding: '2px 12px 8px' }}>
                  {(() => {
                    const label = labels[mobileIndex];
                    const isSelected = label.labelId === selectedLabelId;
                    const goPrev = () => {
                      const next = (mobileIndex - 1 + labels.length) % labels.length;
                      setMobileIndex(next);
                      selectLabel(labels[next]);
                    };
                    const goNext = () => {
                      const next = (mobileIndex + 1) % labels.length;
                      setMobileIndex(next);
                      selectLabel(labels[next]);
                    };
                    return (
                      <div
                        style={{
                          width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 6px 6px 4px',
                          borderRadius: 8, background: isSelected ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.16)',
                        }}
                      >
                        <button type="button" title="Previous label" onClick={goPrev} disabled={labels.length <= 1} style={{ ...carouselArrowStyle, opacity: labels.length <= 1 ? 0.3 : 1 }}>‹</button>
                        <span
                          onClick={() => goToLabelRow(label)}
                          style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', padding: '3px 0' }}
                        >
                          {mobileIndex + 1}. {label.title}
                        </span>
                        {canEdit && (
                          <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button
                              type="button"
                              title="Edit"
                              onClick={(e) => { e.stopPropagation(); setFormState({ mode: 'edit', labelId: label.labelId, title: label.title, description: label.description }); }}
                              style={rowBtnStyle}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></svg>
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(label.labelId); }}
                              style={rowBtnStyle}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                            </button>
                          </span>
                        )}
                        <button type="button" title="Next label" onClick={goNext} disabled={labels.length <= 1} style={{ ...carouselArrowStyle, opacity: labels.length <= 1 ? 0.3 : 1 }}>›</button>
                      </div>
                    );
                  })()}
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center', margin: '6px 0 0' }}>
                    {mobileIndex + 1} / {labels.length}
                  </p>
                </div>
              ) : (
                <div style={{ overflowY: 'auto', padding: '4px 12px 14px' }}>
                  {labels.map((label, index) => {
                    const isSelected = label.labelId === selectedLabelId;
                    return (
                    <div
                      key={label.labelId}
                      onClick={() => goToLabelRow(label)}
                      className="labels-panel-row"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 8,
                        cursor: 'pointer', background: isSelected ? 'rgba(255,255,255,0.14)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? 'rgba(255,255,255,0.14)' : 'transparent'; }}
                    >
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {index + 1}. {label.title}
                      </span>
                      {canEdit && (
                        <span className="labels-panel-row-actions" style={{ display: 'flex', gap: 2, flexShrink: 0, opacity: 0, transition: 'opacity 0.15s ease' }}>
                          <button
                            type="button"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); setFormState({ mode: 'edit', labelId: label.labelId, title: label.title, description: label.description }); }}
                            style={rowBtnStyle}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></svg>
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(label.labelId); }}
                            style={rowBtnStyle}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                          </button>
                        </span>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {!isMobile && addLabelBlock}
            </>
          )}
        </div>
      )}

      {confirmDeleteId && (() => {
        const target = labels.find((l) => l.labelId === confirmDeleteId);
        const targetTitle = (target && target.title && target.title.trim()) || 'Untitled label';
        return (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={modalCard}>
              <p style={modalTitle}>Delete “{targetTitle}”?</p>
              <p style={modalBody}>This label will be permanently removed. This can’t be undone.</p>
              <div style={MODAL_BTN_ROW}>
                <button type="button" onClick={() => setConfirmDeleteId(null)} style={{ ...modalSecondaryBtn, flex: 1 }}>
                  Cancel
                </button>
                <button type="button" onClick={confirmDelete} style={{ ...modalDangerBtn, flex: 1 }}>
                  Delete
                </button>
              </div>
            </div>
          </ModalOverlay>
        );
      })()}
    </>
  );
};

export default LabelsPanel;
